import styled from "@emotion/styled";
import theme from "../../styles/theme";
import { GatsbyImage } from "gatsby-plugin-image";
import * as FA from "../../styles/fontawesome";
import CTA from "../cta/cta";

type DescriptionProps = {
  styleType?: string | null | undefined;
};

export const AdvancedSliceBase = styled.section`
  &.no-top-padding {
    padding-top: 0;
  }

  &.no-bottom-padding {
    padding-bottom: 0;
  }

  &.no-top-and-bottom-padding {
    padding-top: 0;
    padding-bottom: 0;
  }

  &.image-background&:not(.two-column-5-7) {
    padding: 0;
  }

  &.light-blue {
    background-color: ${theme.colors.blues[10]};
  }
`;

export const AdvancedSliceContainer = styled.div`
  max-width: ${theme.maxWidth};
  margin: 0 auto;

  &.narrow {
    max-width: ${theme.narrowContainer};
  }

  &.two-column {
    ${theme.largerThan.md} {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: ${theme.spacing[16]};
      justify-items: start;
      align-content: center;
    }
  }

  &.two-column-5-7 {
    ${theme.largerThan.md} {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: ${theme.spacing[8]};
      justify-items: start;
      align-content: center;

      > .content {
        padding-right: ${theme.spacing[8]};
        grid-column: span 5 / span 5;
      }

      > .items {
        grid-column: span 7 / span 7;
      }
    }
  }
`;

export const AdvancedSliceContent = styled.div`
  position: relative;

  &:not(.image-background):not(.text-only) {
    display: grid;
    grid-template-areas:
      "image"
      "text";
    gap: ${theme.spacing[8]};
    max-width: ${theme.maxWidth};
    margin: 0 auto;
    align-items: flex-start;

    ${theme.largerThan.md} {
      gap: 3rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    ${theme.largerThan.lg} {
      gap: 4rem;
    }
  }

  &.text-only {
    max-width: ${theme.maxWidth};
    margin: 0 auto;
  }

  &.left-align-text:not(.text-only) {
    ${theme.largerThan.md} {
      grid-template-areas: "text image";
    }
  }

  &.right-align-text:not(.text-only) {
    ${theme.largerThan.md} {
      grid-template-areas: "image text";
    }
  }

  &.center-align-text:not(.image-background):not(.text-only) {
    grid-template-areas:
      "image"
      "text";
  }

  &.text-and-links {
  }

  &.twenty-eighty.right-align-text:not(.image-background):not(.text-only) {
    display: grid;
    gap: 1.5rem;

    ${theme.largerThan.md} {
      grid-template-columns: repeat(10, minmax(0, 1fr));
      grid-template-areas: "image image text text text text text text text text";
      align-items: flex-start;

      .text-container {
        padding-left: ${theme.spacing[6]};

        h3 {
          margin-top: 0;
        }
      }
    }
  }

  &.two-column-5-7 {
    > .image-background {
      &:not(.text-container) {
        ${theme.smallerThan.md} {
          display: none;
        }

        ${theme.largerThan.md} {
          position: absolute;
          bottom: 0;
          left: 0;
          max-width: 400px;
        }

        ${theme.largerThan.xl} {
          bottom: 0;
          left: -${theme.spacing[4]};
        }
      }
    }
  }
`;

export const TextContainer = styled.div`
  grid-area: text;
  align-self: center;

  &.center-align-text {
    text-align: center;
    max-width: 700px;
    margin: 0 auto;
  }

  &.image-background&:not(.two-column-5-7) {
    padding: ${theme.spacing[16]} ${theme.spacing[4]};
  }

  &.text-only {
    max-width: 700px;
  }
`;

export const Description = styled.div<DescriptionProps>`
  color: ${theme.colors.darkBlues[90]};
  font-size: ${theme.fontSizes[2]};
  line-height: ${theme.leading.relaxed};
  margin-bottom: ${theme.spacing[4]};

  ${(props) =>
    props.styleType === "styled-list" &&
    `
    ul {
      list-style: none;
      margin: ${theme.spacing[8]} 0;
      padding-left: 0;

      li {
        margin-bottom: ${theme.spacing[4]};

        &:before {
          content: '';
          display: inline-block;
          line-height: 1;
          height: 1.25em;
          background-color: currentColor;
          -webkit-mask: url(https://assets.xometry.com/fontawesome-pro/v6/svgs/regular/check.svg) no-repeat center/contain content-box;
          mask: url(https://assets.xometry.com/fontawesome-pro/v6/svgs/regular/check.svg) no-repeat center/contain content-box;
          -webkit-mask: url(https://assets.xometry.com/fontawesome-pro/v6/svgs/regular/check.svg) no-repeat center/contain content-box;
          aspect-ratio: 448/512;
          vertical-align: -15%;
          background-color: ${theme.colors.greens[100]};
          margin-right: ${theme.spacing[2]};
        }
      }
    }
  `}
`;

export const ImageContainer = styled.div`
  border-radius: ${theme.borderRadius};
  overflow: hidden;

  &.image-background&:not(.two-column-5-7) {
    position: relative;
    height: 300px;
    overflow: hidden;

    ${theme.largerThan.lg} {
      height: 350px;
    }

    @media (min-width: ${theme.breakpoints[5]}px) and (min-height: ${theme
        .breakpoints[4]}px) {
      height: 600px;
    }
  }

  &:not(.image-background) {
    grid-area: image;
    height: 100%;
  }

  ${theme.largerThan.md} {
    &.justify-self-end {
      justify-self: end;
    }
  }

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }

  .gatsby-image-wrapper img {
    object-fit: contain !important;
    border-radius: ${theme.borderRadius};
    overflow: hidden;
  }
`;

export const Image = styled(GatsbyImage)`
  max-width: 100%;

  &.image-background&:not(.two-column-5-7) {
    height: 300px;

    img {
      position: absolute !important;

      ${theme.largerThan.sm} {
        height: auto !important;
      }
    }

    ${theme.largerThan.lg} {
      height: 350px;
    }

    @media (min-width: ${theme.breakpoints[5]}px) and (min-height: ${theme
        .breakpoints[4]}px) {
      height: 600px;
    }

    &.top {
      img {
        top: 0 !important;
      }
    }

    &.middle {
      img {
        top: 50% !important;
        bottom: auto !important;
        transform: translateY(-50%) !important;
      }
    }

    &.bottom {
      img {
        bottom: 0 !important;
        top: auto !important;
      }
    }
  }

  &:not(.image-background) {
    border-radius: 0.25rem;
    max-width: 100%;
    max-height: 500px;
  }

  &.auto {
    width: auto;
  }
`;

export const RepeatableItems = styled.div`
  max-width: ${theme.maxWidth};
  margin: 0 auto;

  &.one-column-grid {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: ${theme.spacing[4]};
  }

  &.two-column-grid,
  &.locations {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: ${theme.spacing[8]};

    ${theme.largerThan.md} {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &.three-column-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing[8]};
    row-gap: ${theme.spacing[8]};

    ${theme.largerThan.md} {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &.four-column-grid,
  &.four-column-grid-icons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing[8]};
    row-gap: ${theme.spacing[8]};

    ${theme.largerThan.md} {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &.four-column-flex {
    display: flex;
    gap: ${theme.spacing[8]};
    flex-wrap: wrap;
    justify-content: center;
    max-width: 1200px;
    margin: 1rem auto 0;
  }

  &.link-list {
    max-width: none;
    margin: 0;
    display: flex;
    align-items: center;
  }
`;

export const RepeatableItem = styled.div`
  font-size: ${theme.fontSizes[2]};
  line-height: ${theme.leading.relaxed};
  color: ${theme.colors.darkBlues[100]};
  margin: 1rem 0;

  &.one-column-grid {
    display: grid;
    grid-template-columns: 70px 1fr;
    gap: 1rem;
  }

  &.two-column-grid {
    ${theme.largerThan.md} {
      display: grid;
      grid-template-columns: 70px 1fr;
      gap: 1rem;
    }
  }

  &.three-column-grid,
  &.four-column-grid {
    display: grid;
    gap: 1rem;
  }

  &.four-column-flex {
    width: calc(50% - ${theme.spacing[8]});
    text-align: center;

    ${theme.largerThan.md} {
      width: calc(25% - ${theme.spacing[8]});
    }
  }

  &.four-column-grid-icons {
    a,
    span {
      display: grid;
      grid-template-columns: 50px 1fr;
      gap: 1rem;
      align-content: center;
      margin: 0;
      text-decoration: none;

      .repeatable-item-image {
        background-color: ${theme.colors.blues[100]};
        width: 50px;
        height: 50px;
        display: grid;
        justify-items: center;
        align-content: center;
        border-radius: 100%;
        margin-bottom: 0;
        box-shadow: ${theme.boxShadow.small};
        transition: all 0.1s ease-out;
      }

      .repeatable-item-text {
        align-self: center;
        color: ${theme.colors.darkBlues[75]};
        line-height: ${theme.leading.normal};
        transition: all 0.1s ease-out;

        p {
          margin: 0;
        }
      }

      &:hover {
        .repeatable-item-image {
          background-color: ${theme.colors.blues[125]};
          transform: scale(1.1);
        }

        .repeatable-item-text {
          color: ${theme.colors.darkBlues[100]};
          transform: translateY(-0.1em);
        }
      }
    }
  }

  &.locations {
    ul {
      list-style: none;
      padding: 0;
      display: grid;
      gap: 1rem;

      ${theme.largerThan.md} {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      li {
        strong {
          display: inline-block;
          margin-bottom: 0.275rem;
        }
      }
    }
  }
`;

export const RepeatableItemImage = styled.div`
  margin-bottom: 0.5rem;
`;

export const RepeatableItemText = styled.div`
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
  }

  h6 {
    margin-bottom: 0;
  }

  ul {
    list-style: none;
    padding-left: 0;
    margin-top: 0;
  }

  ul li {
    margin-bottom: ${theme.spacing[2]};
  }

  a.hyperlink.Web {
    &:after {
      content: "";
      margin-left: ${theme.spacing[2]};
      ${FA.ArrowRightRegular}
    }
  }

  a.hyperlink.Media {
    &:before {
      content: "";
      margin-right: ${theme.spacing[2]};
      ${FA.FileLinesRegular}
    }
  }

  p.paragraph {
    margin-bottom: ${theme.spacing[4]};
  }
`;

export const CTAStyled = styled(CTA)`
  width: fit-content;
  align-self: center;
  padding: 12px 16px !important;

  ${theme.largerThan.sm} {
    align-self: initial;
  }
`;
